import { AnimatePresence, motion } from "framer-motion";
import PlayIcon from "assets/icons/editor/PlayIcon";
import { FaCheck } from "react-icons/fa6";
import { FaStopCircle } from "react-icons/fa";
import { useRef, useState } from "react";
import React from "react";

interface RunCodeButtonType {
  showRunCode: boolean;
  handleRunCode?: () => void;
  prefixId: string;
}

const buttonVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0, transition: { duration: 0.1, ease: "easeOut" } },
};

const RunCodeButton: React.FC<RunCodeButtonType> = ({
  showRunCode,
  handleRunCode,
  prefixId,
}) => {
  // States definition
  const [animationState, setAnimationState] = useState<number>(0);
  // Define a timer ref
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const runCodeAnimation = () => {
    // Clear any existing timeout to prevent overlapping
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set the new state of runCodePressed
    setAnimationState(1);

    // Check the handleRunCode is defined, then execute
    if (handleRunCode !== undefined) {
      handleRunCode();
    }

    // After finish the run code, show the successful animation
    setAnimationState(2);

    // Return to the original state after 2 seconds
    timerRef.current = setTimeout(() => {
      setAnimationState(0);
    }, 2000);

    // And clear the timer
    return () => {if (timerRef.current) clearTimeout(timerRef.current)};
  };

  return (
    <div className={showRunCode ? "top-0" : "top-0 invisible"}>
      <button
        className="flex relative justify-center items-center w-11 h-11 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded-full cursor-pointer"
        onClick={runCodeAnimation}
        title="Run code"
        id={`${prefixId}-run-code`}
      >
        <AnimatePresence mode="wait">
          {animationState === 0 && (
            <motion.div
              key="play"
              initial="initial"
              animate="animate"
              exit="exit"
              variants={buttonVariants}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="absolute flex justify-center items-center w-full h-full"
            >
              <PlayIcon colour="neutral-50" />
            </motion.div>
          )}
          {animationState === 1 && (
            <motion.div
              key="loading"
              initial="initial"
              animate="animate"
              exit="exit"
              variants={buttonVariants}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="absolute flex justify-center items-center w-full h-full"
            >
              <motion.div className="absolute flex justify-center items-center w-full h-full">
                <motion.div
                  className="w-9 h-9 border-[3px] rounded-full border-neutral-50 border-b-transparent"
                  animate={{ rotate: 360 }}
                  transition={{
                    repeat: Infinity,
                    duration: 2,
                    ease: "linear",
                  }}
                />
              </motion.div>
              <FaStopCircle className="text-neutral-50 w-6 h-6" />
            </motion.div>
          )}
          {animationState === 2 && (
            <motion.div
              key="done"
              initial="initial"
              animate="animate"
              exit="exit"
              variants={buttonVariants}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="absolute flex justify-center items-center w-full h-full"
            >
              <motion.div className="absolute flex justify-center items-center w-full h-full">
                <motion.div
                  className="w-9 h-9 border-[3px] rounded-full border-neutral-50"
                  transition={{
                    repeat: 1,
                    duration: 2,
                    ease: "linear",
                  }}
                />
              </motion.div>
              <FaCheck className="text-neutral-50 w-6 h-6" />
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </div>
  );
};

export default RunCodeButton;
